import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  PageTitle, 
  Spinner, 
  EmptyState 
} from '../components';
import { 
  FaBell, 
  FaCheckDouble, 
  FaExclamationCircle 
} from 'react-icons/fa';
import api from '../services/api';
import { formatRelativeTime } from '../utils/formatters';

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);

  // 获取通知数据
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        
        // 获取通知列表
        const response = await api.get('/api/notifications');
        setNotifications(response.data || []);
      } catch (err) {
        console.error('获取通知失败', err);
        setError('获取通知失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotifications();
  }, []);

  // 标记所有通知为已读
  const markAllAsRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      
      // 更新本地状态
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => ({ ...notif, isRead: true }))
      );
    } catch (err) {
      console.error('标记所有通知为已读失败', err);
      setError('操作失败，请稍后再试');
    }
  };

  // 标记单个通知为已读
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/api/notifications/${notificationId}/mark-read`);
      
      // 更新本地状态
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => 
          notif._id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    } catch (err) {
      console.error('标记通知为已读失败', err);
    }
  };

  // 如果正在加载，显示加载中状态
  if (loading) {
    return <Spinner fullPage text="加载通知..." />;
  }

  // 计算未读通知数量
  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  return (
    <div className="notifications-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <PageTitle 
          title="通知中心" 
          subtitle="查看您的所有系统通知"
          icon={<FaBell />}
        />
        
        {unreadCount > 0 && (
          <button 
            className="btn btn-outline-primary"
            onClick={markAllAsRead}
          >
            <FaCheckDouble className="me-1" /> 全部标为已读
          </button>
        )}
      </div>
      
      {/* 错误提示 */}
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          <FaExclamationCircle className="me-2" />
          {error}
        </div>
      )}
      
      {/* 通知列表 */}
      <Card>
        {notifications.length > 0 ? (
          <div className="list-group list-group-flush">
            {notifications.map(notification => (
              <div 
                key={notification._id}
                className={`list-group-item p-3 ${!notification.isRead ? 'notification-unread' : ''}`}
              >
                <div className="d-flex">
                  <div className="me-3">
                    <div 
                      className={`rounded-circle text-white d-flex align-items-center justify-content-center notification-icon notification-${notification.type}`}
                      style={{ width: 48, height: 48 }}
                    >
                      <FaBell size={18} />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <h5 className="mb-0">
                        {notification.title}
                        {!notification.isRead && (
                          <span className="badge bg-danger ms-2">新</span>
                        )}
                      </h5>
                      <small className="text-muted">
                        {formatRelativeTime(notification.createdAt)}
                      </small>
                    </div>
                    <p className="mb-1">{notification.message}</p>
                    <div className="d-flex align-items-center mt-2">
                      {notification.actionUrl && (
                        <Link 
                          to={notification.actionUrl}
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => !notification.isRead && markAsRead(notification._id)}
                        >
                          查看详情
                        </Link>
                      )}
                      
                      {!notification.isRead && (
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => markAsRead(notification._id)}
                        >
                          标为已读
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState 
            type="empty"
            title="暂无通知"
            message="您目前没有任何系统通知。"
          />
        )}
      </Card>
      
      {/* 通知说明 */}
      <div className="mt-4">
        <h5>通知类型说明</h5>
        <div className="row">
          <div className="col-md-4">
            <div className="d-flex align-items-center mb-2">
              <div className="notification-icon notification-change me-2" style={{ width: 24, height: 24 }}></div>
              <span>内容变更通知</span>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center mb-2">
              <div className="notification-icon notification-system me-2" style={{ width: 24, height: 24 }}></div>
              <span>系统通知</span>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center mb-2">
              <div className="notification-icon notification-alert me-2" style={{ width: 24, height: 24 }}></div>
              <span>警报通知</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 添加通知相关CSS */}
      <style jsx="true">{`
        .notification-unread {
          background-color: rgba(13, 110, 253, 0.05);
        }
        .notification-icon {
          background-color: #6c757d;
        }
        .notification-change {
          background-color: #0d6efd;
        }
        .notification-system {
          background-color: #198754;
        }
        .notification-alert {
          background-color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default Notifications; 